
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Row, Col, Spinner, Badge, Container, Button } from "react-bootstrap";
import Swal from "sweetalert2";
import { BASE_URL } from "../../services/Api";
import { authHeader } from "../../services/auth-header";
import { FaArrowLeft, FaEdit, FaTrash } from "react-icons/fa";

const BranchView = () => {
  const { id, pointindex } = useParams();
  const navigate = useNavigate();

  const [branchData, setBranchData] = useState({
    company: null, branchname: "", contactperson: "", phoneno: "", gstno: "", email: "",
    address: "", country: null, state: null, city: null, zipcode: "",
    accountno: "", bankname: "", bankbranchname: "", ifsccode: "", statecode: ""
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchBranchDetails = async () => {
      try {
        const response = await axios.get(`${BASE_URL}branch/findById/${id}`, { headers: authHeader() });
        const branch = response.data;

        setBranchData({
          company: branch.company ? { value: branch.company.id, label: branch.company.companyname } : null,
          branchname: branch.branchname,
          contactperson: branch.contactperson,
          phoneno: branch.phoneno,
          gstno: branch.gstno,
          email: branch.email,
          address: branch.address,
          country: branch.country ? { value: branch.country.id, label: branch.country.countryname } : null,
          state: branch.state ? { value: branch.state.id, label: branch.state.statename, code: branch.state.statecode } : null,
          city: branch.city ? { value: branch.city.id, label: branch.city.cityname } : null,
          zipcode: branch.zipcode,
          accountno: branch.accountno,
          bankname: branch.bankname,
          bankbranchname: branch.bankbranchname,
          ifsccode: branch.ifsccode,
          statecode: branch.statecode || (branch.state ? branch.state.statecode : "")
        });
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to fetch branch details. Please try again.',
        });
      } finally {
        setIsLoading(false);
      }
    };
    fetchBranchDetails();
  }, [id]);

  const handleDelete = (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        axios.delete(BASE_URL + "branch/delete/" + id, { headers: authHeader() })
          .then(response => {
            if (response.data.length === 0) {
              Swal.fire(
                'Warning',
                'Branch present in another module. Delete operation has been cancelled.',
                'warning'
              );
            } else {
              navigate(`/branchindex/${pointindex}`);
              Swal.fire(
                'Deleted!',
                'Branch deleted successfully.',
                'success'
              );
            }
          })
          .catch((error) => {
            Swal.fire(
              'Error',
              error.response?.data || 'Something went wrong',
              'error'
            );
          });
      }
    });
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <Container fluid className="p-3">
      {/* Header Section */}
      <div className="shadow-sm border rounded bg-white mb-3">
        <div className="p-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="fw-bold text-info mb-0">BRANCH - VIEW</h5>
            <div>
              <Button variant="outline-danger" size="sm" className="me-2" onClick={() => handleDelete(id)}>
                <FaTrash className="me-1" />
                Delete
              </Button>
              <Link to={`/branchEdit/${id}/${pointindex}`} className="btn btn-outline-warning btn-sm me-2">
                <FaEdit className="me-1" />
                Edit
              </Link>
              <Link to={`/branchindex/${pointindex}`} className="btn btn-secondary btn-sm">
                <FaArrowLeft className="me-1" />
                Back
              </Link>
            </div>
          </div>

          <Row className="g-2">
            <Col md={3}>
              <div className="border-start border-3 border-info ps-2">
                <small className="text-muted">Branch Name</small>
                <div className="fw-bold">{branchData.branchname || '-'}</div>
              </div>
            </Col>
            <Col md={3}>
              <div className="border-start border-3 border-primary ps-2">
                <small className="text-muted">Company</small>
                <div className="fw-bold">{branchData.company?.label || '-'}</div>
              </div>
            </Col>
            <Col md={3}>
              <div className="border-start border-3 border-success ps-2">
                <small className="text-muted">Contact Person</small>
                <div className="fw-bold">{branchData.contactperson || '-'}</div>
              </div>
            </Col>
            <Col md={3}>
              <div className="border-start border-3 border-warning ps-2">
                <small className="text-muted">Status</small>
                <div>
                  <Badge bg="success" className="px-2 py-1">Active</Badge>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </div>

      {/* Basic Information */}
      <div className="shadow-sm border rounded bg-white mb-3">
        <div className="bg-light py-2 px-3 border-bottom">
          <h6 className="mb-0 fw-bold">Basic Information</h6>
        </div>
        <div className="p-3">
          <Row className="g-3">
            <Col md={4}>
              <small className="text-muted">Branch Name</small>
              <div className="fw-bold">{branchData.branchname || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Company</small>
              <div className="fw-bold">{branchData.company?.label || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Contact Person</small>
              <div className="fw-bold">{branchData.contactperson || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Mobile No</small>
              <div className="fw-bold">{branchData.phoneno || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Email</small>
              <div className="fw-bold">{branchData.email || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">GST No</small>
              <div className="fw-bold">{branchData.gstno || '-'}</div>
            </Col>
          </Row>
        </div>
      </div>

      {/* Address Details */}
      <div className="shadow-sm border rounded bg-white mb-3">
        <div className="bg-light py-2 px-3 border-bottom">
          <h6 className="mb-0 fw-bold">Address Details</h6>
        </div>
        <div className="p-3">
          <Row className="g-3">
            <Col md={12}>
              <small className="text-muted">Address</small>
              <div className="fw-bold">{branchData.address || '-'}</div>
            </Col>
            <Col md={3}>
              <small className="text-muted">Country</small>
              <div className="fw-bold">{branchData.country?.label || '-'}</div>
            </Col>
            <Col md={3}>
              <small className="text-muted">State</small>
              <div className="fw-bold">{branchData.state?.label || '-'}</div>
            </Col>
            <Col md={3}>
              <small className="text-muted">State Code</small>
              <div className="fw-bold">{branchData.statecode || '-'}</div>
            </Col>
            <Col md={3}>
              <small className="text-muted">City</small>
              <div className="fw-bold">{branchData.city?.label || '-'}</div>
            </Col>
            <Col md={3}>
              <small className="text-muted">Zip Code</small>
              <div className="fw-bold">{branchData.zipcode || '-'}</div>
            </Col>
          </Row>
        </div>
      </div>

      {/* Bank Details */}
      <div className="shadow-sm border rounded bg-white mb-3">
        <div className="bg-light py-2 px-3 border-bottom">
          <h6 className="mb-0 fw-bold">Bank Details</h6>
        </div>
        <div className="p-3">
          <Row className="g-3">
            <Col md={4}>
              <small className="text-muted">Bank Name</small>
              <div className="fw-bold">{branchData.bankname || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Account No</small>
              <div className="fw-bold">{branchData.accountno || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Branch Name</small>
              <div className="fw-bold">{branchData.bankbranchname || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">IFSC Code</small>
              <div className="fw-bold">{branchData.ifsccode || '-'}</div>
            </Col>
          </Row>
        </div>
      </div>

      {/* Footer Actions */}
      <div className="text-center mt-3 pt-2 border-top">
        <Link to={`/branchEdit/${id}/${pointindex}`} className="btn btn-info btn-sm me-2 text-white">
          <FaEdit className="me-1" />
          Edit Branch
        </Link>
        <Link to={`/branchindex/${pointindex}`} className="btn btn-secondary btn-sm">
          <FaArrowLeft className="me-1" />
          Back to List
        </Link>
      </div>
    </Container>
  );
};

export default BranchView;